import _extends from '@babel/runtime/helpers/extends';
import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import hoistNonReactStatics from 'hoist-non-react-statics';
import invariant from 'invariant';
import React, { useContext, useState, useDebugValue, useEffect, useLayoutEffect } from 'react';
import { DEFAULT_FEATURE } from '@redux-syringe/namespaces';
import { useNamespace, NamespaceContext } from '@redux-syringe/namespaces-react';
import { capitalize } from '@redux-syringe/utils';
import { getDisplayName } from '@redux-syringe/utils-react';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import { reject, isNil, all, includes } from 'ramda';
import { ReactReduxContext } from 'react-redux';
import { createEntries } from '@redux-syringe/injectors';

var makeDecorator = function makeDecorator(storeInterface, useInjectables) {
  invariant(storeInterface, 'The store interface is falsy.');
  var type = storeInterface.type;
  var decoratorName = type ? "With".concat(capitalize(type)) : 'Injector';
  return function (injectables) {
    var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    return function (NextComponent) {
      var Injector = function Injector(props) {
        var _ref, _options$feature, _ref2, _ref3, _options$namespace;

        // eslint-disable-next-line react/destructuring-assignment
        var feature = (_ref = (_options$feature = options.feature) !== null && _options$feature !== void 0 ? _options$feature : props.feature) !== null && _ref !== void 0 ? _ref : DEFAULT_FEATURE;
        var contextNamespace = useNamespace(feature); // eslint-disable-next-line react/destructuring-assignment

        var namespace = (_ref2 = (_ref3 = (_options$namespace = options.namespace) !== null && _options$namespace !== void 0 ? _options$namespace : props.namespace) !== null && _ref3 !== void 0 ? _ref3 : contextNamespace) !== null && _ref2 !== void 0 ? _ref2 : undefined;
        var isInitialized = useInjectables(injectables, _objectSpread(_objectSpread({}, options), {}, {
          feature: feature,
          namespace: namespace
        }));

        if (isInitialized) {
          return /*#__PURE__*/React.createElement(NextComponent, _extends({
            key: String([feature, namespace])
          }, props));
        }

        return null;
      };

      hoistNonReactStatics(Injector, NextComponent);
      Injector.displayName = "".concat(decoratorName, "(").concat(getDisplayName(NextComponent), ")");
      return Injector;
    };
  };
};

var IS_SERVER = typeof window === 'undefined';

var useUniversalLayoutEffect = IS_SERVER ? useEffect : useLayoutEffect;
var makeHook = function makeHook(storeInterface) {
  invariant(storeInterface, 'The store interface is falsy.');
  var getEntries = storeInterface.getEntries,
      ejectionKey = storeInterface.ejectionKey,
      injectionKey = storeInterface.injectionKey,
      type = storeInterface.type;
  var capitalizedType = capitalize(type);
  var hookName = "use".concat(capitalizedType);

  var useInjectables = function useInjectables(injectables) {
    var _ref, _options$isGlobal, _ref2, _options$isPersistent, _options$isNamespaced, _options$feature, _options$namespace;

    var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    var locationMessages = ["@redux-syringe ".concat(type), injectables];

    var warn = function warn() {
      var _console;

      for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
        args[_key] = arguments[_key];
      }

      return (_console = console).warn.apply(_console, locationMessages.concat(args));
    }; // NOTE: `options.global` and `options.persist` are deprecated.


    var isGlobal = (_ref = (_options$isGlobal = options.isGlobal) !== null && _options$isGlobal !== void 0 ? _options$isGlobal : options.global) !== null && _ref !== void 0 ? _ref : false;
    var isPersistent = (_ref2 = (_options$isPersistent = options.isPersistent) !== null && _options$isPersistent !== void 0 ? _options$isPersistent : options.persist) !== null && _ref2 !== void 0 ? _ref2 : false;
    var isNamespaced = (_options$isNamespaced = options.isNamespaced) !== null && _options$isNamespaced !== void 0 ? _options$isNamespaced : false;
    var feature = (_options$feature = options.feature) !== null && _options$feature !== void 0 ? _options$feature : DEFAULT_FEATURE;
    var contextNamespace = useNamespace(feature);
    var store = useContext(ReactReduxContext).store;

    var _useContext = useContext(NamespaceContext),
        isUseNamespaceProvided = _useContext.isUseNamespaceProvided;

    var namespace = isGlobal ? undefined : (_options$namespace = options.namespace) !== null && _options$namespace !== void 0 ? _options$namespace : contextNamespace;
    var inject = store[injectionKey];
    var eject = store[ejectionKey]; // NOTE: On the server, the injectables should be injected beforehand.

    var _useState = useState(IS_SERVER),
        _useState2 = _slicedToArray(_useState, 2),
        isInitialized = _useState2[0],
        setIsInitialized = _useState2[1];

    var featureAndNamespace = reject(isNil, {
      feature: feature,
      namespace: namespace
    }); // TODO: Refactor when React DevTools support multiple debug values or non-primitive structures.

    useDebugValue(String(["namespace:".concat(namespace), "feature:".concat(feature), "type:".concat(capitalizedType), "initialized:".concat(isInitialized)]));

    if (IS_SERVER) {
      var areEntriesAlreadyInjected = all(function (entry) {
        return includes(entry, getEntries(store));
      }, createEntries(injectables, featureAndNamespace));

      if (!areEntriesAlreadyInjected) {
        warn("When rendering on the server, inject all ".concat(type, " before calling"), "'ReactDOMServer.renderToString()'. You should do this inside an", "'async getInitialProps()' function, i.e. where you fetch data and", 'do other side effects. If you need to do server-side injections', 'during rendering, open an issue.');
      }
    }

    var effectDependencies = [namespace, feature, isGlobal, isPersistent, isNamespaced, inject, eject, injectables]; // NOTE: This doesn't run on the server, but won't trigger `useLayoutEffect` warnings either.

    useUniversalLayoutEffect(function () {
      if (isGlobal && feature !== DEFAULT_FEATURE) {
        warn("You are using a feature (".concat(feature, ") with global ").concat(type, "."), 'This will have no effect.');
      }

      if (isUseNamespaceProvided && isNil(namespace) && !isGlobal) {
        warn("You're injecting ".concat(type, ", but the namespace could not be resolved from React context!"), 'They will be injected globally. If this is intended, consider passing', "'isGlobal: true' to the injector, e.g. '".concat(hookName, "(").concat(type, ", { isGlobal: true })'."));
      }

      if (!isNil(options.global)) {
        warn("'global: ".concat(options.global, "' is deprecated. Use 'isGlobal: ").concat(options.global, "'."));
      }

      if (!isNil(options.persist)) {
        warn("'persist: ".concat(options.persist, "' is deprecated. Use 'isPersistent: ").concat(options.persist, "'."));
      }

      invariant(!isNamespaced || !isNil(namespace), "You're injecting ".concat(type, " marked as namespaced, but no namespace could be resolved."));
      invariant(inject, "'store.".concat(injectionKey, "' missing. Are you using the enhancer correctly?"));
      invariant(eject, "'store.".concat(ejectionKey, "' missing. Are you using the enhancer correctly?"));
      inject(injectables, featureAndNamespace);
      setIsInitialized(true);
      return function () {
        if (!isPersistent) {
          eject(injectables, featureAndNamespace);
        }
      };
    }, effectDependencies);
    return isInitialized;
  };

  return useInjectables;
};

export { makeDecorator, makeHook };
